
import React from "react";

export function useScrollY(): number {
    const [y, setY] = React.useState<number>(window.scrollY);
    React.useEffect(() => {
        const handler = () => setY(window.scrollY);
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, []);
    return y;
}

export function useInView<T extends HTMLElement = HTMLDivElement>(threshold = 0.15): [React.RefObject<T | null>, boolean] {
    const ref = React.useRef<T>(null);
    const [inView, setInView] = React.useState<boolean>(false);
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                obs.disconnect();
            }
        }, { threshold });
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

export function useCounter(target: number, active: boolean, duration = 1800): number {
    const [val, setVal] = React.useState<number>(0);
    React.useEffect(() => {
        if (!active) return;
        let frame = 0;
        let start: number | null = null;
        const step = (ts: number) => {
            if (start === null) start = ts;
            const p = Math.min((ts - start) / duration, 1);
            setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
            if (p < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [target, active, duration]);
    return val;
}

export function useItemsPerView(): number {
    const compute = () => (window.innerWidth <= 600 ? 1 : window.innerWidth <= 900 ? 2 : 3);
    const [count, setCount] = React.useState<number>(compute);
    React.useEffect(() => {
        const handler = () => setCount(compute());
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, []);
    return count;
}
